/*
 * Phase 3.7 — "Bir sorun mu var?" / block "Glossée Uzmanına Sor" (snippets/glossee-nail-expert.liquid).
 *
 * Every quick question, Enter and the arrow button end in ONE function: askGlosseeAssistant(question). It talks to the
 * Glossée Assistant app only through the app's public page API:
 *   GlosseeAssistant.isReady() / GlosseeAssistant.ask({ question })
 *   document events 'glossee-assistant:ready' (once) and 'glossee-assistant:sending' ({ sending })
 * No shadow DOM access, no launcher clicks, no second widget, no polling, no intervals.
 *
 * One customer action sends at most one question: the form is the only submit path (Enter and the button are the same
 * submit), a click or submit is ignored while a question is on its way, the buttons are disabled until the app reports
 * that the answer has arrived, and the app refuses duplicates on its own side as well.
 */
(function () {
  'use strict';

  if (!window.customElements || window.customElements.get('glossee-nail-expert')) return;

  /*
   * AN OPEN CHAT STAYS VISIBLE OVER THE STICKY "SEPETE EKLE" BAR.
   * The Phase 3.6.1 rule (assets/component-cart-widget-overlap-fix.css) hides the whole assistant host above 480px
   * while the product page's sticky bar is revealed -- right for the launcher, wrong for a chat the customer has just
   * opened from this block. The app reports its panel opening and closing (only once the page API has been used);
   * html.gne-assistant-open lets component-glossee-nail-expert.css keep an open panel visible. Registered once per
   * page, after the guard above, so a second evaluation of this file cannot add it twice.
   */
  document.addEventListener('glossee-assistant:panel', function (event) {
    var open = Boolean(event && event.detail && event.detail.open === true);
    document.documentElement.classList.toggle('gne-assistant-open', open);
  });

  var MAX_CHARS = 1000;
  // Buttons stay disabled at least this long after a send, even when the answer is faster (the app uses the same floor).
  var SPACING_MS = 1500;
  // Only if the app never reports the end of a send: buttons come back rather than staying dead.
  var UNLOCK_FALLBACK_MS = 30000;

  var MESSAGES = {
    loading: 'Tırnak Uzmanı hazırlanıyor…',
    busy: 'Tırnak Uzmanı önceki sorunu yanıtlıyor.',
    placed: 'Sorun sohbet kutusuna eklendi. Göndermek için Gönder’e dokun.',
    locked: 'Bu sohbet sona erdi. Tırnak Uzmanı penceresinden yeni bir sohbet başlatabilirsin.',
    unavailable: 'Tırnak Uzmanı şu anda açılamadı. Lütfen biraz sonra tekrar dene.',
    designMode: 'Tema düzenleyicide soru gönderilmez. Mağazada bu alan soruyu Tırnak Uzmanı’na gönderir.'
  };

  function refused(reason) {
    return { opened: false, prefilled: false, sent: false, reason: reason };
  }

  /*
   * THE bridge. Returns the app's own result, or a refusal when the app (or its page API) is not there.
   * The app answers synchronously; it never throws to the page, but a broken or older app must not throw here either.
   */
  function askGlosseeAssistant(question) {
    var api = window.GlosseeAssistant;
    if (!api || typeof api.ask !== 'function' || typeof api.isReady !== 'function') {
      // No app on this page yet, or a version without the page API.
      return refused(api ? 'unavailable' : 'not_ready');
    }
    try {
      if (!api.isReady()) return refused('not_ready');
      var result = api.ask({ question: question });
      return result && typeof result === 'object' ? result : refused('unavailable');
    } catch (error) {
      return refused('unavailable');
    }
  }

  /* The app embed is on this page and its script may still be on its way. */
  function assistantMayStillLoad() {
    return document.readyState !== 'complete' && Boolean(document.getElementById('glossee-assistant-root'));
  }

  class GlosseeNailExpert extends HTMLElement {
    connectedCallback() {
      // Idempotent: a re-connected element (theme editor re-render, DOM move) drops every listener it had first.
      this.teardown();
      this.controller = new AbortController();
      var signal = this.controller.signal;

      this.form = this.querySelector('[data-gne-form]');
      this.input = this.querySelector('[data-gne-input]');
      this.statusEl = this.querySelector('[data-gne-status]');
      this.busy = false;
      this.appSending = false;
      this.lockedAt = 0;
      this.pending = null;
      this.waiting = false;

      this.addEventListener('click', this.onClick.bind(this), { signal: signal });
      if (this.form) this.form.addEventListener('submit', this.onSubmit.bind(this), { signal: signal });
      document.addEventListener('glossee-assistant:sending', this.onSending.bind(this), { signal: signal });
    }

    disconnectedCallback() {
      this.teardown();
    }

    teardown() {
      if (this.controller) this.controller.abort();
      this.controller = null;
      window.clearTimeout(this.spacingTimer);
      window.clearTimeout(this.fallbackTimer);
      this.spacingTimer = null;
      this.fallbackTimer = null;
    }

    onClick(event) {
      var chip = event.target instanceof Element ? event.target.closest('[data-gne-question]') : null;
      if (!chip || !this.contains(chip)) return;
      event.preventDefault();
      this.ask(chip.getAttribute('data-gne-question'), 'chip');
    }

    onSubmit(event) {
      event.preventDefault();
      this.ask(this.input ? this.input.value : '', 'form');
    }

    ask(raw, source) {
      var question = typeof raw === 'string' ? raw.trim() : '';
      if (question.length > MAX_CHARS) question = question.slice(0, MAX_CHARS).trim();
      if (!question) {
        // Empty or whitespace only: nothing is sent and nothing opens.
        if (source === 'form' && this.input) this.input.focus();
        return;
      }
      if (this.busy || this.waiting) return;

      if (this.hasAttribute('data-design-mode') || (window.Shopify && window.Shopify.designMode)) {
        // Clicking around in the theme editor must never cost a real answer.
        this.setStatus(MESSAGES.designMode);
        return;
      }

      this.handle(askGlosseeAssistant(question), question, source);
    }

    handle(result, question, source) {
      if (result.sent) {
        if (source === 'form' && this.input && this.input.value.trim().slice(0, MAX_CHARS).trim() === question) {
          this.input.value = '';
        }
        this.setStatus('');
        this.lockUntilAnswered();
        return;
      }

      switch (result.reason) {
        case 'not_ready':
          this.waitForAssistant(question, source);
          return;
        case 'already_sending':
        case 'duplicate':
          this.setStatus(MESSAGES.busy);
          this.lockUntilAnswered();
          return;
        case 'user_gesture_required':
          this.setStatus(MESSAGES.placed);
          return;
        case 'conversation_locked':
          this.setStatus(MESSAGES.locked);
          return;
        case 'question_empty':
          return;
        default:
          this.setStatus(MESSAGES.unavailable);
      }
    }

    /*
     * The assistant has not mounted yet. Wait for its one ready event, bounded by the page's own load event: no
     * polling, no interval, and never forever. One question waits; further clicks meanwhile are ignored.
     */
    waitForAssistant(question, source) {
      if (!assistantMayStillLoad() || !this.controller) {
        this.setStatus(MESSAGES.unavailable);
        return;
      }
      this.pending = { question: question, source: source };
      this.setStatus(MESSAGES.loading);
      if (this.waiting) return;
      this.waiting = true;

      var signal = this.controller.signal;
      var settle = function () {
        if (!this.waiting) return;
        this.waiting = false;
        var pending = this.pending;
        this.pending = null;
        if (!pending) return;
        var result = askGlosseeAssistant(pending.question);
        if (result.reason === 'not_ready') {
          this.setStatus(MESSAGES.unavailable);
          return;
        }
        this.handle(result, pending.question, pending.source);
      }.bind(this);

      document.addEventListener('glossee-assistant:ready', settle, { once: true, signal: signal });
      window.addEventListener('load', settle, { once: true, signal: signal });
    }

    onSending(event) {
      var sending = Boolean(event && event.detail && event.detail.sending === true);
      this.appSending = sending;
      if (sending) {
        this.lock();
      } else if (this.busy) {
        this.releaseAfterSpacing();
      }
    }

    /*
     * The app reports the real in-flight state synchronously inside ask(): if it says a question is on its way, the
     * buttons wait for its "sending: false"; if it does not (the refusal was only the short spacing), they come back
     * after the spacing.
     */
    lockUntilAnswered() {
      this.lock();
      if (!this.appSending) this.releaseAfterSpacing();
    }

    lock() {
      if (!this.busy) this.lockedAt = Date.now();
      this.busy = true;
      this.setDisabled(true);
      window.clearTimeout(this.spacingTimer);
      window.clearTimeout(this.fallbackTimer);
      this.fallbackTimer = window.setTimeout(this.unlock.bind(this), UNLOCK_FALLBACK_MS);
    }

    releaseAfterSpacing() {
      var wait = Math.max(0, SPACING_MS - (Date.now() - this.lockedAt));
      window.clearTimeout(this.spacingTimer);
      this.spacingTimer = window.setTimeout(this.unlock.bind(this), wait);
    }

    unlock() {
      window.clearTimeout(this.spacingTimer);
      window.clearTimeout(this.fallbackTimer);
      this.spacingTimer = null;
      this.fallbackTimer = null;
      this.busy = false;
      this.setDisabled(false);
      if (this.statusEl && this.statusEl.textContent === MESSAGES.busy) this.setStatus('');
    }

    setDisabled(disabled) {
      this.querySelectorAll('[data-gne-question], [data-gne-send]').forEach(function (button) {
        button.disabled = disabled;
      });
    }

    setStatus(message) {
      if (this.statusEl && this.statusEl.textContent !== message) this.statusEl.textContent = message;
    }
  }

  window.customElements.define('glossee-nail-expert', GlosseeNailExpert);
})();
